import React, { useState, useEffect } from 'react';
import { Grid } from '@material-ui/core';
import { getFormValues, SubmissionError, isValid, isDirty } from 'redux-form';
import { compose } from 'ramda';
import { Button, RemoteSubmit, Modal } from 'components/Base';
import Dashboard from 'components/Layout/Dashboard/Dashboard';
import withMeta from 'hoc/withMeta';
import Form from 'components/Forms';
import RiskAnalysisResults from 'components/RiskAnalysis/RiskAnalysisResults/RiskAnalysisResults';
import Router from 'components/Router/Router';
import api from 'api';
import { Loader } from 'components/Base';
import Store from 'store/store';
import { RiskSummaryType } from 'store/modules/RiskSummary/types';
import { CustomerType } from 'store/modules/Customers/types';
import withCaseInfo from 'hoc/withCaseInfo';
import { formatPercent, unFormatPercent, Notify } from 'utils';
import BottomNavBar from 'components/Cases/BottomNavBar';
import Icons from 'components/Icons';
import { errHelper } from 'utils';
import axios from 'axios';
import RiskToleranceReport from 'components/Modals/RiskToleranceReport';
import { useDispatch } from 'react-redux';
import { getRiskProfiles } from 'components/Cases/utils';
import { useLocation } from 'react-router-dom';
import messages from 'consts/messages';
import AddNotesModal from './AddNotesModal';
export interface RiskSummaryTypeExtendedTimeHorizon extends RiskSummaryType {
  risk_tolerance_customer_2?: number;
}

interface RiskSummaryPageProps {
  riskSummaries: Array<RiskSummaryTypeExtendedTimeHorizon>;
  setRiskSummaries: Function;
  invalidateStoreSubset: Function;
  caseCustomers: Array<CustomerType>;
  values: any;
  caseInfo: any;
  setCustomersResponses: (data: any) => void;
  userResponses: Array<any>;
  valid: boolean;
  dirty: boolean;
  reportImages: any;
  setCapacityForLossConfig: Function;
  nextPage: any;
  setNextPage: Function;
}

const { connect, selectors, actions } = Store;
const { useParams, useHistory } = Router;

const RiskSummaryPage = ({
  riskSummaries,
  setRiskSummaries,
  invalidateStoreSubset,
  caseCustomers,
  caseInfo,
  values,
  userResponses = [],
  valid,
  dirty,
  setCustomersResponses,
  reportImages,
  setCapacityForLossConfig,
  nextPage,
  setNextPage
}: RiskSummaryPageProps) => {
  const dispatch = useDispatch();
  const [isSendingData, setIsSendingData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [initialCustomerObjectives, setInitialCustomerObjectives] =
    useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [isSummaryModelOpen, setIsSummaryModelOpen] = useState(false);
  const history = useHistory();
  const { portfolio } = useParams() as any;

  const directCustomers = caseCustomers.filter(
    (customer: CustomerType) => !customer.is_jointed
  );

  const location = useLocation() as any;

  useEffect(() => {
    if (location.state?.refetch) {
      invalidateStoreSubset(['riskSummaries']);
    }
  }, [location.state]);

  useEffect(() => {
    setNextPage(null);
    setIsLoading(true);

    axios
      .all([
        api.getCaseInfo({ portfolio }),
        dispatch(getRiskProfiles(portfolio) as any),
        api.getCapacityForLossConfig(),
        ...caseCustomers.map((customer: CustomerType) =>
          api.getCustomerQuestionnare({ customerId: customer.id })
        )
      ])
      .then(
        axios.spread(
          (
            caseResponse,
            riskSummaryResponse,
            cflConfigResponse,
            ...customerResponses
          ) => {
            setInitialCustomerObjectives(caseResponse.data.customer_objectives);
            setCapacityForLossConfig(cflConfigResponse.data);
            setCustomersResponses(
              customerResponses.map((response, index) => {
                return {
                  ...response.data.results[0],
                  customer_id: caseCustomers[index].id
                };
              })
            );
            setIsLoading(false);
          }
        )
      )
      .catch((err: any) => {
        setIsLoading(false);
        Notify.error(err.response.data.message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const callRiskSummaries = () => {
    api.getRiskSummaries({ portfolio }).then(response => {
      setRiskSummaries(response.data);
    });
  };

  const createRiskProfile = async () => {
    if (!dirty) {
      if (nextPage) {
        history.push(nextPage);
      }
      return true;
    }

    const newValues = values;
    Object.keys(newValues).forEach(
      key => (newValues[key] = newValues[key] || null)
    );

    const data = {
      customer: newValues.customer,
      portfolio: portfolio,
      time_horizon_interval: newValues.time_horizon_interval,
      required_return:
        newValues.required_return === null
          ? newValues.required_return
          : (unFormatPercent(newValues.required_return) / 100).toFixed(4),
      risk_tolerance: newValues.risk_tolerance,
      risk_capacity:
        newValues.risk_capacity === null
          ? newValues.risk_capacity
          : (unFormatPercent(newValues.risk_capacity) / 100).toFixed(4),
      knowledge_level: newValues.knowledge_level,
      recommended_risk_profile: newValues.recommended_risk_profile,
      selected_cip: newValues.selected_cip,
      knowledge_level_description: newValues.knowledge_level_description
    };

    await api.editCase({
      caseInfo: {
        ...caseInfo,
        customer_objectives: newValues.customer_objectives
      }
    });

    api
      .createRiskProfile(data)
      .then(response => {
        if (caseCustomers.length > 1) {
          api
            .updateRiskSummary2({
              portfolio,
              riskProfileId: response.data.id,
              data: {
                ...response.data,
                customer: caseCustomers[1],
                risk_tolerance: values.risk_tolerance_customer_2
              }
            })
            .then(() => {
              callRiskSummaries();
            });
        } else {
          callRiskSummaries();
        }
        if (nextPage) {
          history.push(nextPage);
        }
        return true;
      })
      .catch((err: any) => {
        setIsSendingData(false);
        throw new SubmissionError(err.response.data);
      });

    return true;
  };

  const submitRiskSummary = () => {
    setIsSendingData(true);
    createRiskProfile();
  };

  const getClientQuestionnaire = (customerId: number) => {
    if (!userResponses.length) return;
    return userResponses.find(
      (questionnaire: any) => questionnaire.customer_id === customerId
    );
  };

  const downloadReport = async (
    customerId: number,
    is_jointed: boolean,
    type: string
  ) => {
    let questionnaire = null;
    let questionnaire2 = null;

    // Await all calls
    const q1 = await getClientQuestionnaire(directCustomers[0].id);
    const q2 = await (directCustomers[1]
      ? getClientQuestionnaire(directCustomers[1].id)
      : null);

    const bothIndividualsExist = q1?.id && q2?.id;

    if (bothIndividualsExist) {
      questionnaire = q1;
      questionnaire2 = q2;
    } else if (is_jointed) {
      const joint = await getClientQuestionnaire(customerId);
      if (joint?.id) {
        questionnaire = joint;
        questionnaire2 = null;
      }
    }

    // Final fallback
    if (!questionnaire) {
      const fallback = await getClientQuestionnaire(customerId);
      if (fallback?.id) {
        questionnaire = fallback;
      }
    }

    // Final safety fallback if everything else failed (shouldn't happen if all are filled)
    if (!questionnaire && !questionnaire2) {
      questionnaire = getClientQuestionnaire(customerId); // Try joint one just in case
    }

    if (!valid) {
      Notify.warn(
        'Cannot download report',
        'Please make sure the Risk Analysis form is filled out.'
      );
      return;
    }

    setLoadingReport(true);

    const response = await createRiskProfile();
    if (!response) {
      return;
    }
    const questionnaireId = questionnaire && questionnaire.id;
    const questionnaire2Id = questionnaire2 && questionnaire2.id;

    return api
      .downloadQuestionnare({
        customerId,
        questionnaireId: questionnaireId || null,
        caseId: portfolio,
        images: reportImages,
        questionnaire2Id: questionnaire2Id || null,
        downloadType: type
      })
      .then((response: any) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const customerData = caseCustomers.find(
          (customer: CustomerType) => customer.id === customerId
        );
        const link = document.createElement('a');
        link.href = url;
        let fileExtension = '.pdf';

        if (type === 'word') {
          fileExtension = '.docx';
        }

        link.setAttribute(
          'download',
          `Risk Analysis Report ${
            customerData ? customerData.display_name : ''
          }${fileExtension}`
        );
        document.body.appendChild(link);
        link.click();
        setLoadingReport(false);
        setReportModalOpen(false);
      })
      .catch((err: any) => {
        setLoadingReport(false);
        setReportModalOpen(false);
        errHelper(err);
      });
  };

  const isJointCustomer = !!caseCustomers.find(customer => customer.is_jointed);
  const jointCustomer = caseCustomers.find(customer => customer.is_jointed);
  const isJointQuestionnaire =
    riskSummaries.length === 1 && riskSummaries[0].customer.is_jointed;
  // @ts-ignore
  const initialValues: any = isJointCustomer
    ? isJointQuestionnaire
      ? {
          customer: jointCustomer,
          required_return:
            !!riskSummaries[0] && !!riskSummaries[0].required_return
              ? formatPercent(Number(riskSummaries[0].required_return) * 100)
              : null,
          knowledge_level:
            !!riskSummaries[0] && !!riskSummaries[0].knowledge_level
              ? riskSummaries[0].knowledge_level
              : null,
          risk_capacity:
            !!riskSummaries[0] && !!riskSummaries[0].risk_capacity
              ? formatPercent(
                  Number(riskSummaries[0].risk_capacity.replace('-', '')) * 100
                )
              : null,
          recommended_risk_profile:
            !!riskSummaries[0] && !!riskSummaries[0].recommended_risk_profile
              ? riskSummaries[0].recommended_risk_profile.toString()
              : null,
          risk_tolerance: riskSummaries[0].risk_tolerance,
          risk_tolerance_customer_2: riskSummaries[0].risk_tolerance,
          selected_cip:
            riskSummaries[0] && riskSummaries[0].selected_cip
              ? riskSummaries[0].selected_cip
              : null,
          time_horizon_interval:
            riskSummaries[0] && riskSummaries[0].time_horizon_interval
              ? riskSummaries[0].time_horizon_interval
              : null,
          customer_objectives: initialCustomerObjectives,
          knowledge_level_description:
            riskSummaries[0] && riskSummaries[0].knowledge_level_description
              ? riskSummaries[0].knowledge_level_description
              : null
        }
      : {
          customer: jointCustomer,
          required_return:
            !!riskSummaries[0] && !!riskSummaries[0].required_return
              ? formatPercent(Number(riskSummaries[0].required_return) * 100)
              : null,
          knowledge_level:
            !!riskSummaries[0] && !!riskSummaries[0].knowledge_level
              ? riskSummaries[0].knowledge_level
              : null,
          risk_capacity:
            !!riskSummaries[0] && !!riskSummaries[0].risk_capacity
              ? formatPercent(
                  Number(riskSummaries[0].risk_capacity.replace('-', '')) * 100
                )
              : null,
          recommended_risk_profile:
            !!riskSummaries[0] && !!riskSummaries[0].recommended_risk_profile
              ? riskSummaries[0].recommended_risk_profile.toString()
              : null,
          risk_tolerance: riskSummaries.find(
            riskSummary => riskSummary.customer.id === directCustomers[0].id
          )
            ? (
                riskSummaries.find(
                  riskSummary =>
                    riskSummary.customer.id === directCustomers[0].id
                ) as any
              ).risk_tolerance
            : null,
          risk_tolerance_customer_2: riskSummaries.find(
            riskSummary => riskSummary.customer.id === directCustomers[1].id
          )
            ? (
                riskSummaries.find(
                  riskSummary =>
                    riskSummary.customer.id === directCustomers[1].id
                ) as any
              ).risk_tolerance
            : null,

          selected_cip:
            riskSummaries[0] && riskSummaries[0].selected_cip
              ? riskSummaries[0].selected_cip
              : null,
          time_horizon_interval:
            riskSummaries[0] && riskSummaries[0].time_horizon_interval
              ? riskSummaries[0].time_horizon_interval
              : null,
          customer_objectives: initialCustomerObjectives,
          knowledge_level_description:
            riskSummaries[0] && riskSummaries[0].knowledge_level_description
              ? riskSummaries[0].knowledge_level_description
              : null
        }
    : {
        customer: caseCustomers[0],
        required_return:
          !!riskSummaries[0] && !!riskSummaries[0].required_return
            ? formatPercent(Number(riskSummaries[0].required_return) * 100)
            : null,
        knowledge_level:
          !!riskSummaries[0] && !!riskSummaries[0].knowledge_level
            ? riskSummaries[0].knowledge_level
            : null,
        risk_capacity:
          !!riskSummaries[0] && !!riskSummaries[0].risk_capacity
            ? formatPercent(
                Number(riskSummaries[0].risk_capacity.replace('-', '')) * 100
              )
            : null,
        recommended_risk_profile:
          !!riskSummaries[0] && !!riskSummaries[0].recommended_risk_profile
            ? riskSummaries[0].recommended_risk_profile.toString()
            : null,
        risk_tolerance: riskSummaries.find(
          riskSummary => riskSummary.customer.id === directCustomers[0].id
        )
          ? (
              riskSummaries.find(
                riskSummary => riskSummary.customer.id === directCustomers[0].id
              ) as any
            ).risk_tolerance
          : null,
        selected_cip:
          riskSummaries[0] && riskSummaries[0].selected_cip
            ? riskSummaries[0].selected_cip
            : null,
        time_horizon_interval:
          riskSummaries[0] && riskSummaries[0].time_horizon_interval
            ? riskSummaries[0].time_horizon_interval
            : null,
        customer_objectives: initialCustomerObjectives,
        knowledge_level_description:
          riskSummaries[0] && riskSummaries[0].knowledge_level_description
            ? riskSummaries[0].knowledge_level_description
            : null
      };

  return (
    <Dashboard>
      <Loader isLoading={isLoading}>
        <div style={{ paddingBottom: '86px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'end',
              alignItems: 'center'
            }}
          ></div>
          <Grid container spacing={3}>
            <Form.RiskSummaryEvalue
              onSubmit={submitRiskSummary}
              initialValues={initialValues}
              createRiskProfile={createRiskProfile}
            />
            <RiskAnalysisResults />
          </Grid>
        </div>

        <Modal
          closeModalFunc={() => setReportModalOpen(false)}
          isOpen={reportModalOpen}
          disableBackdropClick={true}
          disableEscapeKeyDown={true}
        >
          <RiskToleranceReport
            customers={caseCustomers}
            onClose={() => setReportModalOpen(false)}
            downloadReport={downloadReport}
          />
        </Modal>

        <AddNotesModal
          isModalOpen={isSummaryModelOpen}
          closeModal={() => setIsSummaryModelOpen(false)}
        />

        <BottomNavBar
          rightButtons={[
            <RemoteSubmit
              nonSubmitFunc={() => {
                if (
                  (values && values.risk_tolerance === null) ||
                  (values && !values.recommended_risk_profile) ||
                  (values && !values.required_return) ||
                  (values && !values.risk_capacity) ||
                  (values && !values.selected_cip) ||
                  (values && !values.time_horizon_interval) ||
                  (values && !values.knowledge_level)
                ) {
                  return;
                }
              }}
              endIcon={<Icons.RightArrow />}
              key="Next"
              isLoading={isSendingData}
              formName="riskProfile"
              id="save-risk-summary"
              callback={() =>
                history.push(`/${portfolio}/existing-policies/manage-policies`)
              }
            >
              Next
            </RemoteSubmit>,

            <>
              <Button
                key="Add Notes"
                variant="contained"
                color="secondary"
                onClick={() => {
                  setIsSummaryModelOpen(true);
                }}
                style={{ marginRight: '12px' }}
              >
                Add Notes
              </Button>
              <Button
                key="report"
                variant="contained"
                color="secondary"
                isLoading={loadingReport}
                onClick={() => {
                  if (
                    !values.time_horizon_interval ||
                    !values.recommended_risk_profile
                  ) {
                    Notify.warn(
                      'Report unavailable',
                      messages.INSUFFICIENT_RISK_PROFILE_DATA_EVALUE
                    );
                    return;
                  }
                  setReportModalOpen(true);
                }}
              >
                Download Risk Analysis Report
              </Button>
            </>
          ]}
        />
      </Loader>
    </Dashboard>
  );
};

export default compose(
  connect(
    (state: any) => ({
      riskSummaries: selectors.caseSelectors.getRiskSummary(state),
      caseCustomers: selectors.caseSelectors.caseCustomers(state),
      caseInfo: selectors.caseSelectors.case(state),
      values: getFormValues('riskProfile')(state),
      valid: isValid('riskProfile')(state),
      dirty: isDirty('riskProfile')(state),
      userResponses: selectors.questionnareSelectors.getUserResponses(state),
      reportImages: selectors.reportSelectors.getImages(state),
      nextPage: selectors.caseSelectors.nextPage(state)
    }),
    {
      setRiskSummaries: actions.caseActions.setRiskSummaries,
      invalidateStoreSubset: actions.caseActions.invalidateStoreSubset,
      setCustomersResponses: actions.questionnareActions.setCustomersResponses,
      setCapacityForLossConfig: actions.capacityForLossActions.setConfig,
      setNextPage: actions.caseActions.setNextPage
    }
  ),
  withMeta,
  withCaseInfo
)(RiskSummaryPage);
